import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { useTheme, getColors } from "./theme";
import en from "./locales/en.json";
import uk from "./locales/uk.json";

export type Lang = "en" | "uk";
const translations: Record<Lang, Record<string, unknown>> = { en, uk };

interface I18nContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string, params?: Record<string, string>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function getNestedValue(
  obj: Record<string, unknown>,
  path: string,
): string | undefined {
  const parts = path.split(".");
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === "string" ? current : undefined;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const saved = localStorage.getItem("biostream-lang");
    if (saved === "en" || saved === "uk") return saved;
    // Default to Ukrainian
    return "uk";
  });

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    localStorage.setItem("biostream-lang", l);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string>) => {
      let val =
        getNestedValue(translations[lang] as Record<string, unknown>, key) ??
        getNestedValue(translations.uk as Record<string, unknown>, key) ??
        key;
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          val = val.replaceAll(`{${k}}`, v);
        }
      }
      return val;
    },
    [lang],
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

export function LangSwitcher() {
  const { lang, setLang } = useI18n();
  const { theme } = useTheme();
  const colors = getColors(theme);

  const langs: Lang[] = ["uk", "en"];
  const labels: Record<Lang, string> = { uk: "УК", en: "EN" };

  return (
    <span
      style={{
        display: "inline-flex",
        border: `1px solid ${colors.border}`,
        borderRadius: 4,
        overflow: "hidden",
      }}
    >
      {langs.map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          style={{
            padding: "1px 4px",
            border: "none",
            cursor: "pointer",
            background: lang === l ? colors.primary : "transparent",
            color: lang === l ? "#fff" : colors.textMuted,
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          {labels[l]}
        </button>
      ))}
    </span>
  );
}
